import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, Minimize2, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from 'react-router-dom';

const OjasChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const { user, isAuthenticated } = useAuth();
  const location = useLocation();
  const hasAutoOpenedRef = useRef(new Set());

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  // Initial greeting
  useEffect(() => {
    if (messages.length === 0) {
      const greeting = `👋 Welcome to RecruiterIQ${isAuthenticated ? `, ${user?.name || ''}` : ''}!\n\nI'm Ojas, your AI assistant. I can help you with:\n• SmartMatch - Candidate analysis\n• Market Intelligence - Hiring insights\n• Certifications - Career growth\n• And much more!\n\nHow can I help you today?`;
      
      setMessages([{
        id: 1,
        type: 'bot',
        text: greeting,
        timestamp: new Date()
      }]);
    }
  }, []);

  // Auto-open on homepage only
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Only auto-open on homepage and only once (skip on mobile — covers hero)
    if (currentPath === '/' && !hasAutoOpenedRef.current.has('homepage') && window.innerWidth >= 768) {
      const openTimer = setTimeout(() => {
        setIsOpen(true);
        hasAutoOpenedRef.current.add('homepage');
        
        // Auto-close after 5 seconds
        const closeTimer = setTimeout(() => {
          setIsOpen(false);
        }, 5000);
        
        return () => clearTimeout(closeTimer);
      }, 2000);

      return () => clearTimeout(openTimer);
    }
  }, [location.pathname]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async () => {
    const textToSend = inputMessage.trim();
    if (!textToSend) return;

    // Add user message
    const userMessage = {
      id: Date.now(),
      type: 'user',
      text: textToSend,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/ojas/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(isAuthenticated && { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` })
        },
        body: JSON.stringify({
          message: textToSend,
          context: {
            page: location.pathname,
            user_id: user?.id || 'guest',
            user_name: user?.name || 'Guest'
          }
        })
      });

      const data = await response.json();
      
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: data.response || "I'm here to help! What would you like to know?",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botMessage]);

    } catch (error) {
      console.error('Ojas error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        text: "Sorry, I'm having trouble connecting. Please try again!",
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div style={{ position: 'fixed', bottom: '80px', right: '24px', zIndex: 40 }}>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
        >
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(59, 130, 246, 0.5)', animation: 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite' }}></div>
          <MessageCircle style={{ width: '32px', height: '32px', color: 'white', zIndex: 10 }} />
          <div style={{ position: 'absolute', top: '-4px', right: '-4px', width: '14px', height: '14px', background: '#10b981', borderRadius: '50%', border: '2px solid white', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          width: '360px',
          height: '550px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: '1px solid #e2e8f0'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(90deg, #2563eb 0%, #06b6d4 100%)',
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'white',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '32px',
                  height: '32px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <div style={{ display: 'flex', gap: '3px' }}>
                    <div style={{ width: '6px', height: '6px', background: 'white', borderRadius: '50%' }}></div>
                    <div style={{ width: '6px', height: '6px', background: 'white', borderRadius: '50%' }}></div>
                  </div>
                </div>
              </div>
              <div>
                <div style={{ color: 'white', fontWeight: 'bold', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  Ojas <Sparkles style={{ width: '14px', height: '14px' }} />
                </div>
                <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '11px' }}>AI Assistant • Online</div>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                padding: '8px',
                borderRadius: '8px',
                cursor: 'pointer',
                color: 'white'
              }}
            >
              <Minimize2 style={{ width: '20px', height: '20px' }} />
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1,
            overflowY: 'auto',
            padding: '16px',
            background: '#f8fafc'
          }}>
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: '16px'
                }}
              >
                <div style={{
                  maxWidth: '80%',
                  padding: '12px 16px',
                  borderRadius: '16px',
                  background: message.type === 'user' 
                    ? 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)'
                    : 'white',
                  color: message.type === 'user' ? 'white' : '#1e293b',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  border: message.type === 'bot' ? '1px solid #e2e8f0' : 'none'
                }}>
                  <div style={{ fontSize: '14px', whiteSpace: 'pre-wrap' }}>{message.text}</div>
                  <div style={{
                    fontSize: '11px',
                    marginTop: '4px',
                    color: message.type === 'user' ? 'rgba(255,255,255,0.7)' : '#94a3b8'
                  }}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '16px' }}>
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '16px',
                  background: 'white',
                  border: '1px solid #e2e8f0'
                }}>
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <div style={{ width: '8px', height: '8px', background: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out' }}></div>
                    <div style={{ width: '8px', height: '8px', background: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out 0.2s' }}></div>
                    <div style={{ width: '8px', height: '8px', background: '#94a3b8', borderRadius: '50%', animation: 'bounce 1.4s infinite ease-in-out 0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px',
            background: 'white',
            borderTop: '1px solid #e2e8f0'
          }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                style={{
                  flex: 1,
                  padding: '12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: '12px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isTyping}
                style={{
                  padding: '12px',
                  background: inputMessage.trim() && !isTyping
                    ? 'linear-gradient(135deg, #2563eb 0%, #06b6d4 100%)'
                    : '#cbd5e1',
                  border: 'none',
                  borderRadius: '12px',
                  cursor: inputMessage.trim() && !isTyping ? 'pointer' : 'not-allowed',
                  color: 'white'
                }}
              >
                <Send style={{ width: '20px', height: '20px' }} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OjasChat;
